import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetLogin, verifyOtp } from "../../redux/authSlice";
import { decryptUserId } from "../../utilis/encrypto";
import Logo from "../../assets/images/login/logo.png";
import { TextField, Button, Typography, Box } from "@mui/material";
import Grid from "@mui/system/Grid";
import Stack from "@mui/system/Stack";
import AgreeSec from "../agree-sec";
import { useNavigate } from "react-router-dom";
import { ResendOtp } from "../../redux/authSlice";

function VerifyEmailOtp() {
  const { isVerified } = useSelector((state) => state.admin);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState(new Array(4).fill(""));
  const inputRefs = useRef([]);
  const dispatch = useDispatch();

  const [resendDisabled, setResendDisabled] = useState(true); // Start with disabled
  const [timer, setTimer] = useState(60);

  const navigate = useNavigate();

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (/^\d$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next input if not the last one
      if (value && index < 3) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleBackspace = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.includes("")) {
      setError("");
      return;
    }
    const fullOtp = otp.join("");
    const encryptedUserId = localStorage.getItem("userId");
    const userId = decryptUserId(encryptedUserId);

    if (!userId) {
      console.error("Failed to decrypt user ID.");
      return;
    }
    const otpPayload = {
      id: userId,
      otp: fullOtp,
    };

    try {
      await dispatch(verifyOtp(otpPayload));
      dispatch(resetLogin());
      setOtp(new Array(4).fill(""));
      inputRefs.current.forEach((input) => (input.value = ""));
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  const handleResendOtp = async () => {
    const encryptedUserId = localStorage.getItem("userId");
    const userId = decryptUserId(encryptedUserId);

    if (!userId) {
      console.error("Failed to decrypt user ID.");
      return;
    }

    try {
      await dispatch(ResendOtp({ id: userId }));
      setResendDisabled(true); // Disable the button temporarily
      setTimer(60); // Reset the cooldown timer
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  useEffect(() => {
    let interval;
    if (resendDisabled) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval); // Clear the interval on unmount
  }, [resendDisabled]);

  useEffect(() => {
    if (isVerified) {
      navigate("/passwordreset");
    }
  }, [navigate, isVerified]);

  return (
    <Box className="login-wrapper">
      <Grid
        container
        spacing={{ xs: 0, sm: 2 }}
        sx={{ height: { xs: "auto", md: "100%" } }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ m: { xs: "20px", sm: "32px", md: "32px 80px" } }}>
            <img src={Logo} alt="logo" />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack
            sx={{
              height: { xs: "85vh", md: "100%" },
              width: { xs: "90%", sm: "70%", md: "50%" },
              margin: "0 auto",
              position: "relative",
            }}
            justifyContent={{ xs: "center", md: "start" }}
            alignItems={"center"}
          >
            <Box
              sx={{
                width: "100%",
                paddingTop: { xs: "0", sm: "50px", xl: "190px" },
              }}
            >
              <Typography
                variant="h1"
                sx={{ color: "#fff", fontSize: "40px", fontWeight: "700" }}
                fullWidth
              >
                OTP Verification
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#CBD2E2",
                  fontSize: "16px",
                  fontWeight: "500",
                  mt: "12px",
                  mb: "28px",
                }}
              >
                A confirmation OTP has been sent on your registered Email ID
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <Box
                display="flex"
                justifyContent={"space-between"}
                gap={3}
                mb={2}
              >
                {[...Array(4)].map((value, index) => (
                  <TextField
                    key={index}
                    value={otp[index]}
                    inputRef={(ref) => (inputRefs.current[index] = ref)}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleBackspace(e, index)}
                    sx={{
                      "& .MuiInputBase-root": {
                        width: { xs: "50px", xl: "72px" },
                        height: { xs: "50px", xl: "72px" },
                        p: { xs: "10px", xl: "20px" },
                        textAlign: "center",
                        color: "#CBD2E2",
                        fontSize: { xs: "20px", xl: "28px" },
                      },
                    }}
                    inputProps={{
                      maxLength: 1,
                    }}
                  />
                ))}
              </Box>
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Typography
                sx={{
                  color: "#CBD2E2",
                  textAlign: "left",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                Resend OTP in ({timer}s)
              </Typography>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  mb: 2,
                  p: "10px",
                  backgroundColor: "primary.Main",
                  fontSize: "18px",
                  fontWeight: "500",
                  borderRadius: "50px",
                  height: "auto",
                }}
              >
                Send OTP
              </Button>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  sx={{
                    color: "#CBD2E2",
                    textAlign: "center",
                    cursor: "pointer",
                    textDecoration: "none",
                  }}
                >
                  Don’t receive an OTP?{" "}
                  <span
                    style={{
                      color: resendDisabled ? "#B0BEC5" : "#84A1D6",
                    }}
                    onClick={!resendDisabled ? handleResendOtp : undefined}
                  >
                    Resend
                  </span>
                </Typography>
              </Box>
            </Box>

            <AgreeSec />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default VerifyEmailOtp;
